import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { supabase } from '../../shared/supabase/supabaseClient';

const PROJECT_ID = process.env.EXPO_PUBLIC_EAS_PROJECT_ID;
const VAPID_PUBLIC_KEY = process.env.EXPO_PUBLIC_VAPID_PUBLIC_KEY?.trim() ?? '';

const SW_PATH = '/sw.js';

function pushTokenVersionFields(): { app_version?: string; native_build?: string } {
  const app_version = Constants.expoConfig?.version ?? undefined;
  let native_build: string | undefined;
  if (Platform.OS === 'ios') {
    const bn = Constants.expoConfig?.ios?.buildNumber;
    native_build = bn != null && String(bn).trim() !== '' ? String(bn) : undefined;
  } else if (Platform.OS === 'android') {
    const vc = Constants.expoConfig?.android?.versionCode;
    native_build = vc != null ? String(vc) : undefined;
  }
  return {
    ...(app_version ? { app_version } : {}),
    ...(native_build ? { native_build } : {}),
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Uncompressed P-256 VAPID public key is 65 bytes starting with 0x04. */
function isValidVapidPublicKey(key: string): boolean {
  if (!key || key.length < 80) return false;
  try {
    const bytes = urlBase64ToUint8Array(key);
    return bytes.length === 65 && bytes[0] === 0x04;
  } catch {
    return false;
  }
}

function isWebPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Registra web push en la plataforma web.
 * Registra el service worker, pide permiso, suscribe a PushManager y guarda en push_subscriptions.
 */
async function registerWebPush(): Promise<string | null> {
  if (!isWebPushSupported()) return null;
  if (!isValidVapidPublicKey(VAPID_PUBLIC_KEY)) {
    // Missing/placeholder key is expected on web until EXPO_PUBLIC_VAPID_PUBLIC_KEY is set.
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[push] Web push permission not granted');
      return null;
    }

    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });
    }

    const json = sub.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) return null;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const { error } = await supabase
      .from('push_subscriptions')
      .upsert(
        {
          auth_user_id: session.user.id,
          customer_id: null,
          endpoint,
          p256dh_key: p256dh,
          auth_key: auth,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
        },
        { onConflict: 'auth_user_id,endpoint' },
      );

    if (error) {
      console.warn('[push] Web push upsert failed', error.message);
      return null;
    }

    return endpoint;
  } catch (e) {
    console.warn('[push] Web push registration failed', e);
    return null;
  }
}

/**
 * Registra el dispositivo para recibir push notifications.
 * - Native: Expo push token + register-push-token edge function.
 * - Web: Web Push API (service worker + PushManager) + push_subscriptions table.
 */
export async function registerForPushNotificationsAsync(): Promise<string | null> {
  if (Platform.OS === 'web') {
    return registerWebPush();
  }

  if (!Device.isDevice) {
    console.log('[push] Push notifications require a physical device');
    return null;
  }

  const Notifications = await import('expo-notifications');

  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== 'granted') {
    console.log('[push] Permission not granted for push notifications');
    return null;
  }

  // SERVICE_NOT_AVAILABLE en devices sin Play Services / sin red: condición del
  // dispositivo, no bug. Sin este catch el caller void-deja un unhandled rejection
  // (VUELTO-NEGOCIO-3M).
  let token: string;
  try {
    const tokenResponse = await Notifications.getExpoPushTokenAsync({
      projectId: PROJECT_ID,
    });
    token = tokenResponse.data;
  } catch (e) {
    console.warn('[push] Expo push token unavailable', e);
    return null;
  }

  const platform = Platform.OS as 'ios' | 'android' | 'web';

  // Register token with backend
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    console.log('[push] No session, skipping token registration');
    return token;
  }

  try {
    const { error } = await supabase.functions.invoke('register-push-token', {
      body: { token, platform, ...pushTokenVersionFields() },
    });
    if (error) {
      console.error('[push] Failed to register token', error.message);
    }
  } catch (e) {
    console.error('[push] Failed to register token', e);
  }

  return token;
}

/**
 * Elimina el push token del usuario actual del backend.
 * Se llama al cerrar sesión para que un mismo dispositivo no reciba
 * notificaciones de dos cuentas distintas.
 */
export async function unregisterPushNotifications(): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    if (Platform.OS === 'web') {
      // Web: borrar push_subscriptions del usuario
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('auth_user_id', session.user.id);
      return;
    }

    const Notifications = await import('expo-notifications');
    // Native: obtener token actual y borrarlo del backend
    const tokenResponse = await Notifications.getExpoPushTokenAsync({
      projectId: PROJECT_ID,
    });
    const token = tokenResponse.data;
    if (!token) return;

    const { error } = await supabase.functions.invoke('unregister-push-token', {
      body: { token },
    });
    if (error) {
      console.warn('[push] Failed to unregister push token', error.message);
    }
  } catch (e) {
    console.warn('[push] Failed to unregister push token', e);
  }
}

/**
 * Configura listeners para notificaciones recibidas y taps.
 * En web, escucha notificaciones del service worker via message events.
 * Retorna una función de cleanup.
 */
export function setupNotificationListeners(
  onNotification: (title: string, body: string) => void,
  onTap: (data: { orderId?: string; screen?: string; type?: string; appointment_id?: string; starts_at?: string }) => void,
): () => void {
  if (Platform.OS === 'web') {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return () => {};
    }

    const messageHandler = (event: MessageEvent) => {
      const data = event.data;
      if (!data) return;
      if (data.type === 'push-received') {
        onNotification(data.title ?? 'Vuelto', data.body ?? '');
      }
      if (data.type === 'push-click') {
        onTap(data.payload ?? {});
      }
    };
    navigator.serviceWorker.addEventListener('message', messageHandler);

    // Focus listener: cuando la pestaña recupera foco, el SW puede enviar mensajes pendientes
    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
    };
  }

  // Native listeners — sync setup via thenable import is awkward; use require-style promise.
  let receivedSub: { remove: () => void } | null = null;
  let responseSub: { remove: () => void } | null = null;
  let cancelled = false;

  void import('expo-notifications').then((Notifications) => {
    if (cancelled) return;
    receivedSub = Notifications.addNotificationReceivedListener((notification) => {
      const title = notification.request.content.title ?? 'Vuelto';
      const body = notification.request.content.body ?? '';
      onNotification(title, body);
    });

    responseSub = Notifications.addNotificationResponseReceivedListener((response) => {
      const data = response.notification.request.content.data as {
        orderId?: string;
        screen?: string;
        type?: string;
        appointment_id?: string;
        starts_at?: string;
      };
      onTap(data);
    });
  });

  return () => {
    cancelled = true;
    receivedSub?.remove();
    responseSub?.remove();
  };
}
